import type { TranslationKeys } from '@vocab/core';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  Stack,
  Text,
  Box,
  Notice,
  Strong,
  TextLink,
  useResponsiveValue,
} from 'braid-design-system';
import classNames from 'node_modules/classnames';
import {
  useState,
  type FormEventHandler,
  type FormEvent,
  useRef,
  useEffect,
} from 'react';

import ServerError from 'src/components/Account/ServerError/ServerError';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import type { ServerError as ServerErrorType } from 'src/types';

import translations from './.vocab';
import VerificationCodeInput from './VerificationCodeInput';

import * as styles from './styles.css';

export const FORM_NAME = 'changeEmail';

interface Props {
  onSubmit: FormEventHandler;
  onSubmitResend: () => void;
  submitting: boolean;
  toggle: () => void;
  code: string;
  error?: ServerErrorType | null;
  newEmail: string;
  setCode: (state: string) => void;
  setError: (state: ServerErrorType | null) => void;
}

const getClientSideError = (code: string): string => {
  if (!code) {
    return 'Enter your 6 digit code';
  }
  if (code.length < 6) {
    return 'The code should be 6 digits';
  }
  return '';
};

type VerifyCodeFormTranslationKeys = TranslationKeys<typeof translations>;

const Form = ({
  onSubmit,
  onSubmitResend,
  submitting,
  toggle,
  code,
  error,
  newEmail,
  setCode,
  setError,
}: Props) => {
  const { t } = useTranslations(translations);
  const [clientError, setClientError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const { trackAccountLink } = useAnalytics();

  useEffect(() => {
    trackAccountLink({
      eventName: 'email_verification_displayed',
      currentPageSection: 'code verification',
      authenticationFlow: 'changeEmail',
    });
  }, [trackAccountLink]);

  const onCancel = () => {
    trackAccountLink({
      eventName: 'cancel_pressed',
      currentPageSection: 'code verification',
      identityProvider: 'auth0',
      authenticationFlow: 'changeEmail',
    });

    toggle();
  };

  // Auto-submit when code has been entered
  useEffect(() => {
    if (code.length === 6) {
      formRef.current?.requestSubmit();
    }
  }, [code]);

  const formSubmit = (event: FormEvent<Element>) => {
    event.preventDefault();

    const clientSideError = getClientSideError(code);
    setClientError(clientSideError);

    if (clientSideError) {
      trackAccountLink({
        eventName: 'alert_viewed',
        currentPageSection: 'code verification',
        authenticationFlow: 'changeEmail',
        alertShownType: 'notice',
        alertShownPosition: 'content',
        alertShownTitle: 'Invalid code - client side',
        alertShownMessage: clientSideError,
      });

      return;
    }

    onSubmit(event);
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
  };

  const resendCode = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setCode('');
    setClientError('');
    setError(null);

    onSubmitResend();
  };

  const responsiveValue = useResponsiveValue();
  const isTabletOrAbove = responsiveValue({
    mobile: false,
    desktop: true,
    tablet: true,
  });

  const pageContentTranslated = {
    yourVerificationCode: t('Check your email for a code'),
    enterCodeText: t('Enter the 6-digit code we sent to'),
    cantFindText: t(`Can't find it?`),
    takeAMinuteText: t('It may take a minute'),
    resendCodeText: t('Check your spam or resend the code', {
      ResendCodeLink: (v) => (
        <TextLink
          href="/"
          weight="regular"
          onClick={resendCode}
          data={{
            testid: 'resend-code-link',
          }}
        >
          {v}
        </TextLink>
      ),
    }),
    updateBtn: t('Update email'),
    cancelBtn: t('Cancel'),
  };

  return (
    <form onSubmit={formSubmit} ref={formRef}>
      <Stack space="medium">
        <Text weight="strong">
          {pageContentTranslated.yourVerificationCode}
        </Text>

        <Box
          width="full"
          display="flex"
          alignItems="flexStart"
          flexDirection="column"
        >
          <Box width="full">
            <Stack space="small">
              <Box width="full">
                <Text size="small">
                  {pageContentTranslated.enterCodeText}{' '}
                  <Strong>{newEmail}</Strong>
                </Text>
              </Box>

              <Box
                className={classNames(
                  isTabletOrAbove && styles.verificationCodeContainer,
                )}
              >
                <VerificationCodeInput
                  value={code}
                  length={6}
                  validChars="0-9"
                  autoFocus={false}
                  onComplete={handleCodeChange}
                  onChange={handleCodeChange}
                />
              </Box>
            </Stack>

            {clientError ? (
              <div
                data-testid="client-side-error-container"
                className={styles.errorContainer}
              >
                <Notice tone="critical">
                  <Text
                    data={{
                      testid: 'client-side-error',
                    }}
                    size="xsmall"
                  >
                    {t(clientError as VerifyCodeFormTranslationKeys)}
                  </Text>
                </Notice>
              </div>
            ) : (
              <div
                className={classNames(
                  styles.errorContainer,
                  styles.hiddenErrorContainer,
                )}
              />
            )}

            <Box paddingY="small">
              <Text size="small">
                {pageContentTranslated.cantFindText}{' '}
                {pageContentTranslated.takeAMinuteText}{' '}
                {pageContentTranslated.resendCodeText}
              </Text>
            </Box>

            <Box paddingTop="medium">
              <Stack space="small">
                {error && !submitting ? (
                  <ServerError
                    error={error}
                    currentPageSection="code verification"
                  />
                ) : null}
                <Actions>
                  <Button type="submit" tone="formAccent" loading={submitting}>
                    {pageContentTranslated.updateBtn}
                  </Button>
                  <Button
                    variant="transparent"
                    tone="formAccent"
                    onClick={onCancel}
                  >
                    {pageContentTranslated.cancelBtn}
                  </Button>
                </Actions>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Stack>
    </form>
  );
};

export default Form;
